<template>
  <div class="app-container annual-fund-page">
    <el-card shadow="never" class="query-card">
      <div class="query-row">
        <div>
          <div class="page-title">年度合同回款统计</div>
          <div class="page-desc">按实际回款年份查询，并按合同签约年份、回款部门和月份汇总。</div>
        </div>
        <div class="query-actions">
          <span class="query-label">回款年份</span>
          <el-date-picker
            v-model="fundYear"
            type="year"
            value-format="yyyy"
            placeholder="请选择年份"
            :clearable="false"
            style="width: 150px"
            @change="queryList"
          />
          <el-button type="primary" icon="el-icon-search" @click="queryList">查询</el-button>
        </div>
      </div>
    </el-card>

    <el-row :gutter="16" class="summary-row">
      <el-col :xs="24" :sm="8">
        <el-card shadow="never">
          <div class="summary-label">{{fundYear}}年回款总额</div>
          <div class="summary-value">{{amountFormat(totalAmount)}}<span> 元</span></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8">
        <el-card shadow="never">
          <div class="summary-label">涉及合同年份</div>
          <div class="summary-value">{{groupedRows.length}}<span> 个</span></div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8">
        <el-card shadow="never">
          <div class="summary-label">涉及回款部门</div>
          <div class="summary-value">{{departmentCount}}<span> 个</span></div>
        </el-card>
      </el-col>
    </el-row>

    <el-card
      v-for="group in groupedRows"
      :key="group.contractYear"
      shadow="never"
      class="statistics-card"
    >
      <div slot="header" class="card-header">
        <span>{{group.contractYear}}年合同回款情况</span>
        <span class="card-header-desc">统计 {{fundYear}} 年实际回款</span>
      </div>
      <el-table
        :data="group.rows"
        border
        show-summary
        :summary-method="getSummaries"
        v-loading="loading"
      >
        <el-table-column prop="fundDeptName" label="回款部门" fixed="left" min-width="160"/>
        <el-table-column
          v-for="month in months"
          :key="month.value"
          :prop="month.value"
          :label="month.label"
          min-width="120"
          align="right"
        >
          <template slot-scope="scope">{{amountFormat(scope.row[month.value])}}</template>
        </el-table-column>
        <el-table-column prop="totalAmount" label="全年合计" fixed="right" min-width="140" align="right">
          <template slot-scope="scope">{{amountFormat(scope.row.totalAmount)}}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card v-if="!loading && !groupedRows.length" shadow="never" class="empty-card">
      <el-empty :description="`${fundYear}年暂无回款统计数据`"/>
    </el-card>
  </div>
</template>

<script>
import { queryAnnualContractFundStatistics } from '@/api/crm/finance'
import { amountFormat } from '@/utils/index'

export default {
  name: 'AnnualContractFund',
  data () {
    const currentYear = String(new Date().getFullYear())
    return {
      loading: false,
      fundYear: currentYear,
      rows: [],
      months: Array.from({ length: 12 }, (item, index) => ({
        value: `month${index + 1}`,
        label: `${index + 1}月`
      }))
    }
  },
  computed: {
    groupedRows () {
      const groupMap = {}
      this.rows.forEach(row => {
        const key = String(row.contractYear)
        if (!groupMap[key]) {
          groupMap[key] = []
        }
        groupMap[key].push(row)
      })
      return Object.keys(groupMap)
        .sort((a, b) => Number(b) - Number(a))
        .map(contractYear => ({
          contractYear,
          rows: groupMap[contractYear]
        }))
    },
    totalAmount () {
      return this.rows.reduce((total, row) => total + Number(row.totalAmount || 0), 0)
    },
    departmentCount () {
      const deptSet = new Set()
      this.rows.forEach(row => {
        deptSet.add(row.fundDeptId === null || row.fundDeptId === undefined
          ? `name:${row.fundDeptName}`
          : `id:${row.fundDeptId}`)
      })
      return deptSet.size
    }
  },
  created () {
    this.queryList()
  },
  methods: {
    amountFormat,
    queryList () {
      if (!this.fundYear) {
        return
      }
      this.loading = true
      queryAnnualContractFundStatistics({ fundYear: Number(this.fundYear) }).then(response => {
        this.rows = response.data || []
      }).finally(() => {
        this.loading = false
      })
    },
    getSummaries ({ columns, data }) {
      return columns.map((column, index) => {
        if (index === 0) {
          return '合计'
        }
        const total = data.reduce((sum, row) => sum + Number(row[column.property] || 0), 0)
        return this.amountFormat(total)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.annual-fund-page {
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.query-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.page-title {
  color: #1f2d3d;
  font-size: 20px;
  font-weight: 600;
}

.page-desc,
.card-header-desc {
  margin-top: 8px;
  color: #8492a6;
  font-size: 13px;
}

.query-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.query-label {
  color: #606266;
  white-space: nowrap;
}

.summary-row {
  margin-top: 16px;
}

.summary-row .el-card,
.statistics-card,
.empty-card {
  margin-bottom: 16px;
}

.summary-label {
  color: #909399;
  font-size: 14px;
}

.summary-value {
  margin-top: 12px;
  color: #303133;
  font-size: 26px;
  font-weight: 600;
}

.summary-value span {
  color: #909399;
  font-size: 13px;
  font-weight: normal;
}

.card-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.card-header-desc {
  margin-top: 0;
  font-weight: normal;
}

@media (max-width: 768px) {
  .query-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .query-actions {
    flex-wrap: wrap;
  }
}
</style>
